import { useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import { useModuleVisibility } from '../../hooks/useModuleVisibility';
import cx from 'classnames';
import toast from 'react-hot-toast';
import { useData } from '../context/DataContext';
import { useEndpoints } from '../../hooks/useEndpoints';
import { IUser2 } from '../../models/user copy';
import styles from './AthleteSettingsModal.module.scss';

interface AthleteSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  athleteId: string;
  athleteName: string;
}

interface ProfileFormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  birthdayDate: string;
  medicalCertificateExpiry: string;
}

export default function AthleteSettingsModal({ isOpen, onClose, athleteId, athleteName }: AthleteSettingsModalProps) {
  const { testPreferences, athleteTestPreferences, setAthleteTestPreferences } = useData();
  const endpoints = useEndpoints();
  const { isTabVisible } = useModuleVisibility();

  // Test tab state
  const currentAthletePreference = athleteTestPreferences[athleteId]?.rsiTest;
  const teamDefault = testPreferences.rsiTest;
  const [selectedTest, setSelectedTest] = useState<'test-cmj' | 'test-dropjump'>(
    currentAthletePreference || teamDefault
  );

  const [activeTab, setActiveTab] = useState<'profile' | 'account' | 'test' | 'settings'>('test');

  // Athlete data
  const [athlete, setAthlete] = useState<IUser2 | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Profile tab state
  const [profileForm, setProfileForm] = useState<ProfileFormState>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    birthdayDate: '',
    medicalCertificateExpiry: '',
  });

  // Settings tab state
  const [accountState, setAccountState] = useState<'active' | 'inactive'>('active');

  const fetchAthlete = useCallback(async () => {
    if (!athleteId) return;
    setLoading(true);
    try {
      const res = await endpoints.getUsers({
        _id: athleteId,
        populate: 'image,teams,spaceId',
      });
      const docs = res.data?.docs ?? [];
      const user: IUser2 = docs[0];
      if (user) {
        setAthlete(user);
        const bd = (user as any).birthdayDate;
        const certRaw = (user as any).medicalCertificateExpiry;
        setProfileForm({
          firstName: user.firstName ?? '',
          lastName: user.lastName ?? '',
          email: user.email ?? '',
          phone: (user as any).phone ?? '',
          birthdayDate: bd ? new Date(bd).toISOString().split('T')[0] : '',
          medicalCertificateExpiry: certRaw ? new Date(certRaw).toISOString().split('T')[0] : '',
        });
        setAccountState((user.state as 'active' | 'inactive') ?? 'active');
      }
    } catch {
      toast.error('Errore nel caricamento dei dati atleta');
    } finally {
      setLoading(false);
    }
  }, [athleteId, endpoints]);

  useEffect(() => {
    if (isOpen) {
      fetchAthlete();
    }
  }, [isOpen, fetchAthlete]);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (activeTab === 'test') {
      setAthleteTestPreferences(athleteId, { rsiTest: selectedTest });
      toast.success('Preferenze test salvate');
      onClose();
      return;
    }

    if (activeTab === 'profile') {
      setSaving(true);
      try {
        await endpoints.patchUser(athleteId, {
          firstName: profileForm.firstName,
          lastName: profileForm.lastName,
          phone: profileForm.phone,
          birthdayDate: profileForm.birthdayDate || null,
        });
        await endpoints.updateCertificateExpiry(
          athleteId,
          profileForm.medicalCertificateExpiry || null,
        );
        toast.success('Profilo aggiornato con successo');
        onClose();
      } catch {
        toast.error('Errore nel salvataggio del profilo');
      } finally {
        setSaving(false);
      }
      return;
    }

    if (activeTab === 'settings') {
      setSaving(true);
      try {
        await endpoints.patchUser(athleteId, { state: accountState });
        toast.success('Stato account aggiornato');
      } catch {
        toast.error('Errore nel salvataggio');
      } finally {
        setSaving(false);
      }
      return;
    }
  };

  const handleDeactivate = async () => {
    if (!window.confirm(`Sei sicuro di voler disattivare ${athleteName}?`)) return;
    setSaving(true);
    try {
      await endpoints.patchUser(athleteId, { state: 'inactive' });
      setAccountState('inactive');
      toast.success('Atleta disattivato');
    } catch {
      toast.error('Errore nella disattivazione');
    } finally {
      setSaving(false);
    }
  };

  const showFooterSave = activeTab !== 'account';

  const getTypeLabel = (type?: string) => {
    switch (type) {
      case 'athlete': return 'Atleta';
      case 'coach': return 'Coach';
      case 'administrator': return 'Amministratore';
      default: return type ?? '—';
    }
  };

  const getStateLabel = (state?: string) => {
    switch (state) {
      case 'active': return 'Attivo';
      case 'inactive': return 'Inattivo';
      default: return state ?? '—';
    }
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        {/* Header */}
        <div className={styles.header}>
          <h2 className={styles.title}>Impostazioni atleta</h2>
          <button onClick={onClose} className={styles.closeBtn}>
            <X className={styles.closeIcon} />
          </button>
        </div>

        {/* Tabs — i tab con flag false vengono nascosti */}
        <div className={styles.tabs}>
          {isTabVisible('athlete-settings-profile') && (
            <button
              onClick={() => setActiveTab('profile')}
              className={cx(styles.tabBtn, activeTab === 'profile' && styles.tabBtnActive)}
            >
              Profilo
            </button>
          )}
          {isTabVisible('athlete-settings-account') && (
            <button
              onClick={() => setActiveTab('account')}
              className={cx(styles.tabBtn, activeTab === 'account' && styles.tabBtnActive)}
            >
              Account e teams
            </button>
          )}
          {isTabVisible('athlete-settings-test') && (
            <button
              onClick={() => setActiveTab('test')}
              className={cx(styles.tabBtn, activeTab === 'test' && styles.tabBtnActive)}
            >
              Test
            </button>
          )}
          {isTabVisible('athlete-settings-settings') && (
            <button
              onClick={() => setActiveTab('settings')}
              className={cx(styles.tabBtn, activeTab === 'settings' && styles.tabBtnActive)}
            >
              Impostazioni
            </button>
          )}
        </div>

        {/* Content */}

        {/* TAB: TEST */}
        {activeTab === 'test' && (
          <div className={styles.tabContent}>
            <div className={styles.sectionLabel}>
              <h3 className={styles.sectionTitle}>Test RSI per Readiness</h3>
              <p className={styles.sectionDesc}>
                Seleziona quale test utilizzare per il calcolo dell'RSI nel monitoraggio quotidiano
              </p>
            </div>

            <div className={styles.radioList}>
              {/* CMJ Option */}
              <label className={styles.radioLabel}>
                <div className={styles.radioIndicatorWrapper}>
                  <input
                    type="radio"
                    name="rsiTest"
                    value="test-cmj"
                    checked={selectedTest === 'test-cmj'}
                    onChange={(e) => setSelectedTest(e.target.value as 'test-cmj')}
                    className={styles.radioInputHidden}
                  />
                  <div className={cx(styles.radioOuter, selectedTest === 'test-cmj' && styles.radioOuterChecked)} />
                  <div className={styles.radioInnerWrapper}>
                    <div className={cx(styles.radioDot, selectedTest === 'test-cmj' ? styles.radioDotChecked : styles.radioDotUnchecked)} />
                  </div>
                </div>
                <div className={styles.radioTextGroup}>
                  <p className={styles.radioMainText}>CMJ (Countermovement Jump)</p>
                  {!currentAthletePreference && teamDefault === 'test-cmj' && (
                    <span className={styles.radioSubText}>Team default</span>
                  )}
                </div>
              </label>

              {/* Drop Jump Option */}
              <label className={styles.radioLabel}>
                <div className={styles.radioIndicatorWrapper}>
                  <input
                    type="radio"
                    name="rsiTest"
                    value="test-dropjump"
                    checked={selectedTest === 'test-dropjump'}
                    onChange={(e) => setSelectedTest(e.target.value as 'test-dropjump')}
                    className={styles.radioInputHidden}
                  />
                  <div className={cx(styles.radioOuter, selectedTest === 'test-dropjump' && styles.radioOuterChecked)} />
                  <div className={styles.radioInnerWrapper}>
                    <div className={cx(styles.radioDot, selectedTest === 'test-dropjump' ? styles.radioDotChecked : styles.radioDotUnchecked)} />
                  </div>
                </div>
                <div className={styles.radioTextGroup}>
                  <p className={styles.radioMainText}>Drop Jump</p>
                  {!currentAthletePreference && teamDefault === 'test-dropjump' && (
                    <span className={styles.radioSubText}>Team default</span>
                  )}
                </div>
              </label>
            </div>
          </div>
        )}

        {/* TAB: PROFILO */}
        {activeTab === 'profile' && (
          <div className={styles.tabContent}>
            {loading ? (
              <div className={styles.spinnerWrapper}>
                <div className={styles.spinner} />
              </div>
            ) : (
              <>
                <div className={styles.sectionLabel}>
                  <h3 className={styles.sectionTitle}>Dati personali</h3>
                  <p className={styles.sectionDesc}>
                    Modifica le informazioni anagrafiche dell'atleta
                  </p>
                </div>

                <div className={styles.formGrid}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Nome</label>
                    <input
                      type="text"
                      className={styles.formInput}
                      value={profileForm.firstName}
                      onChange={(e) => setProfileForm((p) => ({ ...p, firstName: e.target.value }))}
                      placeholder="Nome"
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Cognome</label>
                    <input
                      type="text"
                      className={styles.formInput}
                      value={profileForm.lastName}
                      onChange={(e) => setProfileForm((p) => ({ ...p, lastName: e.target.value }))}
                      placeholder="Cognome"
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Email</label>
                    <input
                      type="email"
                      className={cx(styles.formInput, styles.formInputDisabled)}
                      value={profileForm.email}
                      readOnly
                      disabled
                      placeholder="Email"
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Telefono</label>
                    <input
                      type="tel"
                      className={styles.formInput}
                      value={profileForm.phone}
                      onChange={(e) => setProfileForm((p) => ({ ...p, phone: e.target.value }))}
                      placeholder="Telefono"
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Data di nascita</label>
                    <input
                      type="date"
                      className={styles.formInput}
                      value={profileForm.birthdayDate}
                      onChange={(e) => setProfileForm((p) => ({ ...p, birthdayDate: e.target.value }))}
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Scadenza certificato medico</label>
                    <input
                      type="date"
                      className={styles.formInput}
                      value={profileForm.medicalCertificateExpiry}
                      onChange={(e) => setProfileForm((p) => ({ ...p, medicalCertificateExpiry: e.target.value }))}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* TAB: ACCOUNT E TEAMS */}
        {activeTab === 'account' && (
          <div className={styles.tabContent}>
            {loading ? (
              <div className={styles.spinnerWrapper}>
                <div className={styles.spinner} />
              </div>
            ) : (
              <>
                <div className={styles.sectionLabel}>
                  <h3 className={styles.sectionTitle}>Account</h3>
                  <p className={styles.sectionDesc}>
                    Informazioni sull'account e i team associati
                  </p>
                </div>

                <div className={styles.infoList}>
                  <div className={styles.infoRow}>
                    <span className={styles.infoKey}>Tipo account</span>
                    <span className={cx(styles.badge, styles.badgeType)}>
                      {getTypeLabel((athlete as any)?.type)}
                    </span>
                  </div>

                  <div className={styles.infoRow}>
                    <span className={styles.infoKey}>Stato</span>
                    <span className={cx(
                      styles.badge,
                      (athlete as any)?.state === 'active' ? styles.badgeActive : styles.badgeInactive
                    )}>
                      {getStateLabel((athlete as any)?.state)}
                    </span>
                  </div>

                  <div className={styles.infoRow}>
                    <span className={styles.infoKey}>Verificato</span>
                    <span className={cx(
                      styles.badge,
                      (athlete as any)?.isVerified ? styles.badgeActive : styles.badgeInactive
                    )}>
                      {(athlete as any)?.isVerified ? 'Si' : 'No'}
                    </span>
                  </div>

                  {(() => {
                    const spaces = (athlete as any)?.spaceId;
                    const spaceList = Array.isArray(spaces) ? spaces : spaces ? [spaces] : [];
                    if (spaceList.length === 0) return null;
                    return (
                      <div className={styles.infoRow}>
                        <span className={styles.infoKey}>Spazio</span>
                        <div className={styles.chipList}>
                          {spaceList.map((s: any, i: number) => (
                            <span key={i} className={styles.chip}>
                              {s?.name ?? s}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {(() => {
                  const teams = (athlete as any)?.teams;
                  const teamList = Array.isArray(teams) ? teams : teams ? [teams] : [];
                  if (teamList.length === 0) return null;
                  return (
                    <div className={styles.accountSection}>
                      <div className={styles.sectionLabel}>
                        <h3 className={styles.sectionTitle}>Team</h3>
                      </div>
                      <div className={styles.teamList}>
                        {teamList.map((t: any, i: number) => (
                          <div key={i} className={styles.teamCard}>
                            <span className={styles.teamName}>{t?.name ?? t}</span>
                            {t?.level && (
                              <span className={styles.teamLevel}>{t.level}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {(() => {
                  const roles = (athlete as any)?.roles;
                  const roleList = Array.isArray(roles) ? roles : [];
                  if (roleList.length === 0) return null;
                  return (
                    <div className={styles.accountSection}>
                      <div className={styles.sectionLabel}>
                        <h3 className={styles.sectionTitle}>Ruoli</h3>
                      </div>
                      <div className={styles.chipList}>
                        {roleList.map((r: string, i: number) => (
                          <span key={i} className={styles.chip}>{r}</span>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </>
            )}
          </div>
        )}

        {/* TAB: IMPOSTAZIONI */}
        {activeTab === 'settings' && (
          <div className={styles.tabContent}>
            {loading ? (
              <div className={styles.spinnerWrapper}>
                <div className={styles.spinner} />
              </div>
            ) : (
              <>
                <div className={styles.sectionLabel}>
                  <h3 className={styles.sectionTitle}>Stato account</h3>
                  <p className={styles.sectionDesc}>
                    Attiva o disattiva l'accesso dell'atleta alla piattaforma
                  </p>
                </div>

                <div className={styles.stateSelector}>
                  <label className={cx(styles.stateOption, accountState === 'active' && styles.stateOptionSelected)}
                    onClick={() => setAccountState('active')}>
                    <div className={styles.radioIndicatorWrapper}>
                      <div className={cx(styles.radioOuter, accountState === 'active' && styles.radioOuterChecked)} />
                      <div className={styles.radioInnerWrapper}>
                        <div className={cx(styles.radioDot, accountState === 'active' ? styles.radioDotChecked : styles.radioDotUnchecked)} />
                      </div>
                    </div>
                    <div>
                      <p className={styles.radioMainText}>Attivo</p>
                      <p className={styles.sectionDesc}>L'atleta ha accesso alla piattaforma</p>
                    </div>
                  </label>

                  <label className={cx(styles.stateOption, accountState === 'inactive' && styles.stateOptionSelected)}
                    onClick={() => setAccountState('inactive')}>
                    <div className={styles.radioIndicatorWrapper}>
                      <div className={cx(styles.radioOuter, accountState === 'inactive' && styles.radioOuterChecked)} />
                      <div className={styles.radioInnerWrapper}>
                        <div className={cx(styles.radioDot, accountState === 'inactive' ? styles.radioDotChecked : styles.radioDotUnchecked)} />
                      </div>
                    </div>
                    <div>
                      <p className={styles.radioMainText}>Inattivo</p>
                      <p className={styles.sectionDesc}>L'atleta non ha accesso alla piattaforma</p>
                    </div>
                  </label>
                </div>

                <div className={styles.dangerZone}>
                  <div className={styles.sectionLabel}>
                    <h3 className={cx(styles.sectionTitle, styles.dangerTitle)}>Zona pericolosa</h3>
                    <p className={styles.sectionDesc}>
                      Le azioni seguenti sono irreversibili
                    </p>
                  </div>
                  <button
                    className={styles.btnDanger}
                    onClick={handleDeactivate}
                    disabled={saving}
                  >
                    Disattiva atleta
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Buttons */}
        <div className={styles.footer}>
          <button onClick={onClose} className={styles.btnSecondary}>Indietro</button>
          {showFooterSave && (
            <button onClick={handleSave} className={styles.btnPrimary} disabled={saving}>
              {saving ? 'Salvataggio...' : 'Salva modifiche'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
